import React, { useState, useEffect } from 'react';
import { emailNotificationService, EmailNotificationSettings } from '../services/emailNotificationService';

const EmailNotifications: React.FC = () => {
  const [settings, setSettings] = useState<EmailNotificationSettings>({
    notification_from_email: null,
    notification_from_name: null,
    contact_form_to_email: null,
    contact_form_cc_emails: null,
    enable_contact_notifications: true,
    newsletter_form_to_email: null,
    newsletter_form_cc_emails: null,
    enable_newsletter_notifications: true,
    quiz_form_to_email: null,
    quiz_form_cc_emails: null,
    enable_quiz_notifications: true,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // State for editing CC email lists
  const [contactCcInput, setContactCcInput] = useState('');
  const [newsletterCcInput, setNewsletterCcInput] = useState('');
  const [quizCcInput, setQuizCcInput] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await emailNotificationService.getSettings();
      setSettings(data);
      setTestEmail(data.notification_from_email || '');
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSuccessMessage('');
      setErrorMessage('');

      await emailNotificationService.updateSettings(settings);
      setSuccessMessage('Email notification settings saved successfully!');

      // Reload to get the latest data
      await loadSettings();
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      setErrorMessage('Please enter a test email address');
      return;
    }

    try {
      setSendingTest(true);
      setSuccessMessage('');
      setErrorMessage('');

      await emailNotificationService.sendTestEmail(testEmail);
      setSuccessMessage(`Test email sent successfully to ${testEmail}!`);
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setSendingTest(false);
    }
  };

  const addContactCc = () => {
    if (contactCcInput.trim()) {
      const currentList = settings.contact_form_cc_emails || [];
      setSettings({
        ...settings,
        contact_form_cc_emails: [...currentList, contactCcInput.trim()],
      });
      setContactCcInput('');
    }
  };

  const removeContactCc = (index: number) => {
    const currentList = settings.contact_form_cc_emails || [];
    setSettings({
      ...settings,
      contact_form_cc_emails: currentList.filter((_, i) => i !== index),
    });
  };

  const addNewsletterCc = () => {
    if (newsletterCcInput.trim()) {
      const currentList = settings.newsletter_form_cc_emails || [];
      setSettings({
        ...settings,
        newsletter_form_cc_emails: [...currentList, newsletterCcInput.trim()],
      });
      setNewsletterCcInput('');
    }
  };

  const removeNewsletterCc = (index: number) => {
    const currentList = settings.newsletter_form_cc_emails || [];
    setSettings({
      ...settings,
      newsletter_form_cc_emails: currentList.filter((_, i) => i !== index),
    });
  };

  const addQuizCc = () => {
    if (quizCcInput.trim()) {
      const currentList = settings.quiz_form_cc_emails || [];
      setSettings({
        ...settings,
        quiz_form_cc_emails: [...currentList, quizCcInput.trim()],
      });
      setQuizCcInput('');
    }
  };

  const removeQuizCc = (index: number) => {
    const currentList = settings.quiz_form_cc_emails || [];
    setSettings({
      ...settings,
      quiz_form_cc_emails: currentList.filter((_, i) => i !== index),
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <p className="mt-4 text-gray-600">Loading email notification settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Email Notifications</h1>
        <p className="mt-2 text-gray-600">
          Configure email notifications for contact forms, newsletters, and quiz completions
        </p>
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative">
          {successMessage}
          <button
            onClick={() => setSuccessMessage('')}
            className="absolute top-0 right-0 p-3 text-green-700"
          >
            ×
          </button>
        </div>
      )}

      {errorMessage && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          {errorMessage}
          <button
            onClick={() => setErrorMessage('')}
            className="absolute top-0 right-0 p-3 text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {/* Setup Instructions */}
      <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">📧 Setup Instructions</h3>
        <ol className="list-decimal list-inside space-y-2 text-blue-800 text-sm">
          <li>Get your Resend API key from <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="underline">resend.com</a></li>
          <li>Add <code className="bg-blue-100 px-2 py-1 rounded">RESEND_API_KEY=re_xxxxx</code> to your backend <code className="bg-blue-100 px-2 py-1 rounded">.env</code> file</li>
          <li>Configure the settings below and click Save</li>
          <li>Use the "Send Test Email" button to verify everything works</li>
        </ol>
      </div>

      <div className="space-y-8">
        {/* From Email Settings */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">From Email Settings</h2>
          <p className="text-sm text-gray-600 mb-6">
            Configure who emails appear to be from
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Email *
              </label>
              <input
                type="email"
                value={settings.notification_from_email || ''}
                onChange={(e) => setSettings({ ...settings, notification_from_email: e.target.value })}
                placeholder="notifications@yourschool.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <p className="mt-1 text-sm text-gray-500">
                Note: With Resend's free tier, you may need to use their onboarding domain initially
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Name
              </label>
              <input
                type="text"
                value={settings.notification_from_name || ''}
                onChange={(e) => setSettings({ ...settings, notification_from_name: e.target.value })}
                placeholder="Armenta Learning Academy"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Contact Form Notifications */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Contact Form Notifications</h2>
              <p className="text-sm text-gray-600 mt-1">
                Get notified when someone submits the contact form
              </p>
            </div>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enable_contact_notifications}
                onChange={(e) => setSettings({ ...settings, enable_contact_notifications: e.target.checked })}
                className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Enable</span>
            </label>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Recipient Email
              </label>
              <input
                type="email"
                value={settings.contact_form_to_email || ''}
                onChange={(e) => setSettings({ ...settings, contact_form_to_email: e.target.value })}
                placeholder="admin@yourschool.com"
                disabled={!settings.enable_contact_notifications}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CC Recipients (Optional)
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={contactCcInput}
                  onChange={(e) => setContactCcInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addContactCc()}
                  placeholder="staff@yourschool.com"
                  disabled={!settings.enable_contact_notifications}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <button
                  onClick={addContactCc}
                  disabled={!settings.enable_contact_notifications || !contactCcInput.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Add
                </button>
              </div>
              {settings.contact_form_cc_emails && settings.contact_form_cc_emails.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {settings.contact_form_cc_emails.map((email, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
                    >
                      {email}
                      <button
                        onClick={() => removeContactCc(index)}
                        className="text-purple-600 hover:text-purple-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Newsletter Form Notifications */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Newsletter Form Notifications</h2>
              <p className="text-sm text-gray-600 mt-1">
                Get notified when someone subscribes to your newsletter
              </p>
            </div>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enable_newsletter_notifications}
                onChange={(e) => setSettings({ ...settings, enable_newsletter_notifications: e.target.checked })}
                className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Enable</span>
            </label>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Recipient Email
              </label>
              <input
                type="email"
                value={settings.newsletter_form_to_email || ''}
                onChange={(e) => setSettings({ ...settings, newsletter_form_to_email: e.target.value })}
                placeholder="marketing@yourschool.com"
                disabled={!settings.enable_newsletter_notifications}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CC Recipients (Optional)
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={newsletterCcInput}
                  onChange={(e) => setNewsletterCcInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addNewsletterCc()}
                  placeholder="team@yourschool.com"
                  disabled={!settings.enable_newsletter_notifications}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <button
                  onClick={addNewsletterCc}
                  disabled={!settings.enable_newsletter_notifications || !newsletterCcInput.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Add
                </button>
              </div>
              {settings.newsletter_form_cc_emails && settings.newsletter_form_cc_emails.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {settings.newsletter_form_cc_emails.map((email, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
                    >
                      {email}
                      <button
                        onClick={() => removeNewsletterCc(index)}
                        className="text-purple-600 hover:text-purple-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quiz Completion Notifications */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Quiz Completion Notifications</h2>
              <p className="text-sm text-gray-600 mt-1">
                Get notified when someone completes the diagnostic quiz
              </p>
            </div>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enable_quiz_notifications}
                onChange={(e) => setSettings({ ...settings, enable_quiz_notifications: e.target.checked })}
                className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Enable</span>
            </label>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Recipient Email
              </label>
              <input
                type="email"
                value={settings.quiz_form_to_email || ''}
                onChange={(e) => setSettings({ ...settings, quiz_form_to_email: e.target.value })}
                placeholder="admin@yourschool.com"
                disabled={!settings.enable_quiz_notifications}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CC Recipients (Optional)
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={quizCcInput}
                  onChange={(e) => setQuizCcInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addQuizCc()}
                  placeholder="team@yourschool.com"
                  disabled={!settings.enable_quiz_notifications}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <button
                  onClick={addQuizCc}
                  disabled={!settings.enable_quiz_notifications || !quizCcInput.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Add
                </button>
              </div>
              {settings.quiz_form_cc_emails && settings.quiz_form_cc_emails.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {settings.quiz_form_cc_emails.map((email, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
                    >
                      {email}
                      <button
                        onClick={() => removeQuizCc(index)}
                        className="text-purple-600 hover:text-purple-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Test Email */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Send Test Email</h2>
          <p className="text-sm text-gray-600 mb-6">
            Verify your email configuration by sending a test email
          </p>

          <div className="flex gap-4">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="your-email@example.com"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <button
              onClick={handleSendTest}
              disabled={sendingTest || !testEmail}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {sendingTest ? 'Sending...' : 'Send Test Email'}
            </button>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          <button
            onClick={loadSettings}
            disabled={saving}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailNotifications;
